import { createGeneralProductInformationValidation } from './general-product-information-validator'

const DEFAULT_SAVE_DELAY = 2500

const createGeneralProductInformationController = ({
    context,
    setDirty,
    setSaving,
    publishEffect,
    reportError,
    saveDelay = DEFAULT_SAVE_DELAY,
}) => {
    let saveTimeoutId = null
    let pendingSave = null
    let hasUnsavedChanges = false
    let validation = createGeneralProductInformationValidation(false)
    let disposed = false

    const saveCard = () => {
        if (context.services && typeof context.services.saveProductCard === 'function') {
            return context.services.saveProductCard()
        }

        if (context.product && typeof context.product.saveCard === 'function') {
            return context.product.saveCard()
        }

        throw new Error('General Product Information requires services.saveProductCard() or product.saveCard()')
    }

    const performSave = () => {
        if (pendingSave) {
            return pendingSave
        }

        setSaving(true)
        pendingSave = Promise.resolve()
            .then(() => saveCard())
            .then(() => publishEffect({
                type: 'product.card.changed',
                phase: 'committed',
                scopes: ['product.card'],
            }))
            .then(() => {
                hasUnsavedChanges = false
                setDirty(false)
                reportError(null)
            })
            .catch(error => {
                reportError(error)
                throw error
            })
            .finally(() => {
                pendingSave = null
                setSaving(false)
            })

        return pendingSave
    }

    const scheduleSave = () => {
        if (disposed) {
            return Promise.resolve()
        }

        hasUnsavedChanges = true
        setDirty(true)
        publishEffect({
            type: 'product.card.changed',
            phase: 'draft',
            scopes: ['product.card'],
        }).catch(reportError)
        clearTimeout(saveTimeoutId)
        saveTimeoutId = setTimeout(() => {
            saveTimeoutId = null
            performSave().catch(() => undefined)
        }, saveDelay)

        return Promise.resolve()
    }

    return Object.freeze({
        setValidation(nextValidation) {
            validation = nextValidation
        },
        validate() {
            return Promise.resolve(validation)
        },
        scheduleSave,
        flush() {
            if (saveTimeoutId !== null) {
                clearTimeout(saveTimeoutId)
                saveTimeoutId = null
            }

            return hasUnsavedChanges ? performSave() : Promise.resolve()
        },
        dispose() {
            disposed = true
            clearTimeout(saveTimeoutId)
            saveTimeoutId = null
        },
    })
}

export { DEFAULT_SAVE_DELAY, createGeneralProductInformationController }
export default createGeneralProductInformationController
